/**
 * Brokerage soft-warn surfaces — Phase-1 NON-BLOCKING advisories.
 *
 * These render the effective brokerage standing of a property/owner/company as
 * a badge or an alert. They NEVER block an action — publishing an ad, closing a
 * deal, or anything else proceeds regardless. (Hard enforcement is a later
 * phase, deliberately gated out here.)
 *
 * Noise control — the surfaces are "self-silencing" so a company that does not
 * use brokerage contracts sees nothing, WITHOUT a feature flag:
 *   - Badges render only when a contract actually exists for the subject.
 *   - The "no active contract" advisory (ad publish) only appears once the
 *     company has adopted brokerage (≥1 contract) — otherwise it stays hidden.
 *   - The dashboard/owner roll-ups only appear when something is expiring/expired.
 * All data is permission-gated (`contracts`) and fail-closed.
 *
 * See: src/hooks/useBrokerageContracts.ts (status + gated-list hooks)
 *      apps/api/src/brokerage-contracts/status-engine.ts (server source of truth)
 */
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useBrokerageContractsGated,
  usePropertyBrokerageStatus,
  useUnitBrokerageStatus,
} from "@/hooks/useBrokerageContracts";
import { effectiveBrokerageStatus } from "@/lib/brokerageBloodline";
import { BrokerageStatusBadge } from "./BrokerageStatusBadge";
import {
  BROKERAGE_STATUS_NONE,
  BrokeragePropertyCoverage,
  BrokerageUnitCoverage,
} from "@dallali/shared";
import type {
  PropertyBrokerageStatus,
  UnitBrokerageStatus,
} from "@dallali/shared";

// ─── Property card badge (pure — fed by the batched resolver) ────────────────

/**
 * Effective brokerage status of one property, as a small badge. PURE: the
 * caller supplies the (batched) status so a grid never fans out N+1 requests.
 * Renders nothing when there is no contract (`none`/undefined) so non-adopters
 * stay clean.
 *
 * Coverage: a property whose contracts only reach SOME of its units still reads
 * `active` (an authorising contract does touch it) — so the badge also shows the
 * covered fraction, e.g. "Active · 3/8 units". Without it, one unit-scoped
 * contract would make a whole building look brokered.
 */
export function PropertyBrokerageBadge({
  status,
  className,
}: {
  status: PropertyBrokerageStatus | undefined;
  className?: string;
}) {
  const { t } = useLanguage();
  if (!status || status.status === BROKERAGE_STATUS_NONE) return null;

  const isPartial =
    status.coverage === BrokeragePropertyCoverage.Partial &&
    typeof status.covered_unit_count === "number" &&
    typeof status.total_unit_count === "number" &&
    status.total_unit_count > 0;

  return (
    <span className="inline-flex items-center gap-1">
      <BrokerageStatusBadge status={status.status} className={className} />
      {isPartial && (
        <Badge
          variant="outline"
          className="gap-1"
          data-testid="brokerage-coverage-badge"
          title={t(
            "بعض الوحدات فقط مشمولة بعقد وساطة",
            "Only some units are covered by a brokerage contract",
          )}
        >
          {/* LTR island: the fraction must not reorder in Arabic. */}
          <span dir="ltr">
            {status.covered_unit_count}/{status.total_unit_count}
          </span>
          {t("وحدة", "units")}
        </Badge>
      )}
    </span>
  );
}

// ─── Unit badge (pure — fed by the batched unit resolver) ────────────────────

/**
 * Effective brokerage status of one UNIT, as a small badge. PURE, for the same
 * N+1 reason as PropertyBrokerageBadge — unit grids pass the batched status in.
 *
 * Self-silencing: renders nothing when the unit has no contract reaching it.
 * That includes the case that motivated all this — a property whose only
 * contract is scoped to a SIBLING unit leaves this unit correctly blank.
 *
 * `coverage` explains WHY the unit is covered: its own contract ("unit") or one
 * inherited from a property-wide contract ("property"). Surfaced as a tooltip
 * here (space is tight in a card) and spelled out in the unit's contract card.
 */
export function UnitBrokerageBadge({
  status,
  className,
}: {
  status: UnitBrokerageStatus | undefined;
  className?: string;
}) {
  const { t } = useLanguage();
  if (!status || status.status === BROKERAGE_STATUS_NONE) return null;

  const inherited = status.coverage === BrokerageUnitCoverage.Property;

  return (
    <span className="inline-flex items-center gap-1" data-testid="unit-brokerage-badge">
      <BrokerageStatusBadge
        status={status.status}
        className={className}
      />
      {status.marketing_authorised && (
        <Badge
          variant="outline"
          className="gap-1"
          data-testid="unit-brokerage-marketing-badge"
        >
          {t("تسويق", "Marketing")}
        </Badge>
      )}
      {inherited && (
        <span
          className="text-xs text-muted-foreground"
          title={t(
            "مشمولة ضمن عقد وساطة على العقار بالكامل",
            "Covered by a brokerage contract on the whole property",
          )}
        >
          {t("(من العقار)", "(from property)")}
        </span>
      )}
    </span>
  );
}

// ─── Ad publish advisory (single property) ───────────────────────────────────

/**
 * Non-blocking advisory shown on the ad-wizard review/publish step when the
 * subject of the ad has no active contract authorising MARKETING — but only once
 * the company has adopted brokerage. Publishing is never blocked.
 *
 * When the ad is for a specific UNIT, pass `unitId`: authorisation is then read
 * at unit level. This matters — a property-wide contract authorises every unit
 * under it, but a contract scoped to unit A does NOT authorise marketing unit B.
 * Checking only the property would wave that second case through.
 */
export function AdPublishBrokerageWarning({
  propertyId,
  unitId,
}: {
  propertyId: string | undefined;
  unitId?: string | undefined;
}) {
  const { t } = useLanguage();
  const { hasPermission } = usePermissions();
  const canView = hasPermission("contracts");

  const { data: contracts } = useBrokerageContractsGated();
  // Unit-scoped ads ask about the unit; property-scoped ads ask about the
  // property. Only one of the two queries is ever enabled.
  const { data: propertyStatus } = usePropertyBrokerageStatus(
    canView && !unitId ? propertyId : undefined,
  );
  const { data: unitStatus } = useUnitBrokerageStatus(
    canView ? unitId : undefined,
  );
  const status = unitId ? unitStatus : propertyStatus;

  const adopted = (contracts?.length ?? 0) > 0;
  // Hidden for non-adopters, the unauthorised-but-fine case, and authorised subjects.
  if (!canView || !adopted || !status || status.marketing_authorised) return null;

  return (
    <Alert
      className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100 [&>svg]:text-amber-600"
      data-testid="brokerage-ad-publish-warning"
    >
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>
        {t("لا يوجد عقد وساطة فعّال للتسويق", "No active brokerage contract for marketing")}
      </AlertTitle>
      <AlertDescription>
        {unitId
          ? t(
              "لا تملك هذه الوحدة عقد وساطة فعّالاً يخوّل عمليات التسويق. يمكنك المتابعة والنشر، لكن يُنصح بربط عقد وساطة ساري المفعول.",
              "This unit has no active brokerage contract authorising marketing. You can still publish, but linking a valid brokerage contract is recommended.",
            )
          : t(
              "لا يملك هذا العقار عقد وساطة فعّالاً يخوّل عمليات التسويق. يمكنك المتابعة والنشر، لكن يُنصح بربط عقد وساطة ساري المفعول.",
              "This property has no active brokerage contract authorising marketing. You can still publish, but linking a valid brokerage contract is recommended.",
            )}
      </AlertDescription>
    </Alert>
  );
}

// ─── Owner directory badge (roll-up over the owner's contracts) ──────────────

/**
 * Small warning badge on an owner card when any of that owner's brokerage
 * contracts expires within the renewal window (`effectiveBrokerageStatus` →
 * "expiring", 60 days). Already-expired contracts are not actionable here.
 * Reads the cached contract list (no extra request) and is permission-gated.
 */
export function OwnerBrokerageBadge({ ownerId }: { ownerId: string }) {
  const { t } = useLanguage();
  const { data: contracts } = useBrokerageContractsGated();
  if (!contracts || contracts.length === 0) return null;

  const now = Date.now();
  const count = contracts.filter(
    (c) =>
      c.owner_id === ownerId &&
      effectiveBrokerageStatus(c, now) === "expiring",
  ).length;
  if (count === 0) return null;

  return (
    <Badge
      variant="warning"
      className="gap-1 whitespace-nowrap"
      data-testid="owner-brokerage-badge"
    >
      <AlertTriangle className="h-3 w-3" />
      {t(`${count} وساطة تنتهي قريباً`, `${count} brokerage expiring`)}
    </Badge>
  );
}

// ─── Dashboard roll-up alert ─────────────────────────────────────────────────

/**
 * Dashboard renewal banner: brokerage contracts expiring within 60 days, so
 * they can be renewed in time. Expired contracts are deliberately excluded —
 * they are history, not an action. Hidden for non-adopters and when nothing is
 * expiring. Links to the brokerage list. Never blocks anything.
 */
export function DashboardBrokerageAlert() {
  const { t } = useLanguage();
  const { data: contracts } = useBrokerageContractsGated();
  if (!contracts || contracts.length === 0) return null;

  const now = Date.now();
  const expiring = contracts.filter(
    (c) => effectiveBrokerageStatus(c, now) === "expiring",
  ).length;
  if (expiring === 0) return null;

  return (
    <Alert
      className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100 [&>svg]:text-amber-600"
      data-testid="dashboard-brokerage-alert"
    >
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{t("عقود وساطة تحتاج تجديد", "Brokerage contracts due for renewal")}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center gap-2">
        <span>
          {t(
            `${expiring} تنتهي خلال 60 يوماً`,
            `${expiring} expiring within 60 days`,
          )}
        </span>
        <Link to="/dashboard/brokerage" className="font-medium underline">
          {t("عرض عقود الوساطة", "View brokerage contracts")}
        </Link>
      </AlertDescription>
    </Alert>
  );
}
